import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import * as d3 from 'd3';

const dropColor = '#A06600';
const centerColor = '#4B7AAA';

let map,
  bounds = [
    [-120.081786, 32.0], // Southwest coordinates
    [-113.988302, 35.879721], // Northeast coordinates
  ],
  pointData = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [0, 0],
        },
        properties: {},
      },
    ],
  },
  nps = [],
  fc = {
    type: 'FeatureCollection',
    features: [],
  },
  routes = {
    type: 'FeatureCollection',
    features: [],
  },
  currentMouse;

let vc = true,
  db = true;

const addMarker = () => {
  // add location marker
  map.addLayer({
    id: 'points',
    type: 'circle',
    source: 'point',
    paint: {
      'circle-radius': 7,
      'circle-color': '#04b867',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    },
  });
};

const stringifyCoords = (coords) => {
  return coords.join(',');
};

const addRoutes = () => {
  map.addSource('routes', {
    type: 'geojson',
    data: routes,
  });

  map.addLayer(
    {
      id: 'routes-layer',
      type: 'line',
      source: 'routes',
      layout: {
        'line-round-limit': 5,
      },
      paint: {
        'line-color': [
          'match',
          ['get', 'type'],
          'driving',
          '#1b9e77',
          'cycling',
          '#d95f02',
          'walking',
          '#7570b3',
          /* other */ '#ccc',
        ],
        'line-offset': [
          'match',
          ['get', 'type'],
          'driving',
          0,
          'cycling',
          4,
          'walking',
          8,
          /* other */ 0,
        ],
        'line-width': 1.5,
        'line-opacity': [
          'case',
          ['boolean', ['feature-state', 'active'], false],
          1,
          0,
        ],
        // 'circle-stroke-color': '#000000',
      },
    },
    'locations-layer'
  );
};

const calculateRoutes = (coordArray) => {
  routes.features = [];
  const currentPoint = pointData.features[0].geometry.coordinates;
  const dirType = ['driving', 'walking', 'cycling'];
  coordArray.forEach((item) => {
    dirType.forEach((dt) => {
      (async () => {
        const route = await fetch(
          `https://api.mapbox.com/directions/v5/mapbox/${dt}/${stringifyCoords(
            currentPoint
          )};${stringifyCoords(item.coords)}?geometries=geojson&access_token=${
            mapboxgl.accessToken
          }`
        ).then((response) => {
          return response.json();
        });
        const methods = ['driving', 'cycling', 'walking'];
        let tmp = {
          type: 'Feature',
          id: item.id,
          properties: {
            type: dt,
            distance: route.routes[0].distance,
            duration: route.routes[0].duration,
            sortOrder: methods.indexOf(dt),
          },
          geometry: route.routes[0].geometry,
        };

        routes.features.push(tmp);
        if (routes.features.length === 15) {
          createList(fc.features, routes);
          addRoutes();
        }
      })();
    });
  });
};

const roundDistance = (n) => {
  return n.toFixed(2);
};

const createList = (locations, routes) => {
  let list = d3.select('#list ul');
  list.html('');

  let li = list
    .selectAll('li')
    .data(locations.slice(0, 5))
    .enter()
    .append('li')
    .attr('id', (d) => `item-${d.id}`)
    .on('mouseover', (e, d) => {
      d3.selectAll('li').classed('active', false);
      d3.select(`#item-${d.id}`).classed('active', true);
      routes.features
        .filter((r) => r.id === d.id)
        .forEach((r) => {
          map.setFeatureState(
            {
              source: 'routes',
              id: r.id,
            },
            {
              active: true,
            }
          );
        });
    })
    .on('mouseout', (e, d) => {
      d3.selectAll('li').classed('active', false);
      routes.features.forEach((r) => {
        map.setFeatureState(
          {
            source: 'routes',
            id: r.id,
          },
          {
            active: false,
          }
        );
      });
    });

  let name = li
    .append('div')
    .classed('name', true)
    .html((d) => d.properties.name);

  let address = li
    .append('div')
    .classed('address', true)
    .html((d) => d.properties.address);

  let distanceBlock = li.append('div').classed('distanceBlock', true);

  let distance = distanceBlock
    .append('span')
    .classed('distance', true)
    .html(
      (d) =>
        `${roundDistance(
          nps.find((n) => n.id === d.id)['distance']
        )} miles away`
    );

  let routeItems = distanceBlock.append('span').classed('routes', true);

  let spans = routeItems
    .selectAll('.transport-modes')
    .data((d) =>
      routes.features
        .filter((r) => r.id === d.id)
        .sort((a, b) =>
          a.properties.sortOrder > b.properties.sortOrder ? 1 : -1
        )
    )
    .enter();

  spans
    .append('span')
    .classed('transport-modes', true)
    .html((d) => {
      let icon = '🚙';
      // let mode = 'car';
      if (d.properties.type === 'cycling') {
        icon = '🚲';
        // mode = 'bike';
      } else if (d.properties.type === 'walking') {
        icon = '🚶';
        // mode = 'foot';
      }
      return `${icon} ${Math.round(d.properties.duration / 60)} mins`;
    });

  let details = li
    .append('div')
    .attr('class', (d) => `details ${d.properties.type}`);

  let type = details
    .append('span')
    .attr('class', (d) => `type ${d.properties.type}`)
    .html((d) => d.properties.type);

  let info = details
    .append('span')
    .classed('info', true)
    .html((d) => d.properties.info);
};

const createNumbersLayer = (features) => {
  map.addSource('numbers', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: features.slice(0, 5),
    },
  });
  map.addLayer({
    id: 'numbers-layer',
    type: 'symbol',
    source: 'numbers',
    paint: {
      'text-opacity': 1,
      'text-color': '#fff',
    },
    layout: {
      'text-field': ['get', 'number'],
      'text-size': 14,
      'text-font': ['Open Sans Bold'],
    },
  });
};

const getNearestPoints = () => {
  // gets points nearest to dropped pin
  let options = { units: 'miles' };
  let nearestPoints = [];
  const point = pointData.features[0];
  let types;
  if (vc && db) {
    types = [
      'Vote Center',
      'Vote Center - Drive thru',
      'Vote Center - Pop Up Voting',
      'Mega Vote Center',
      'Voter Assistance Center',
      'In-Person Voting',
      'Vote Early Site',
      'Ballot Drop Box',
      'Ballot Drop-Box',
      'Ballot Drop-Off',
    ];
  } else if (vc) {
    types = [
      'Vote Center',
      'Vote Center - Drive thru',
      'Vote Center - Pop Up Voting',
      'Mega Vote Center',
      'Voter Assistance Center',
      'In-Person Voting',
      'Vote Early Site',
    ];
  } else if (db) {
    types = ['Ballot Drop Box', 'Ballot Drop-Box', 'Ballot Drop-Off'];
  }

  window.LOCATIONS.features
    .filter((f) => f.properties.county === point.properties.county)
    .filter((f) => types.indexOf(f.properties.type) > -1)
    .forEach((f) => {
      let dist = turf.distance(point, f, options);
      nearestPoints.push({
        id: f.id,
        distance: dist,
      });
    });

  if (nearestPoints.length > 0) {
    nearestPoints.sort(function (a, b) {
      return a.distance - b.distance;
    });

    // get ids of five nearest points
    nps = nearestPoints.slice(0, 5);
    const npsIds = nps.map((item) => item.id);

    // add these five points to the feature layer
    fc.features = window.LOCATIONS.features.filter((f) =>
      npsIds.includes(f.id)
    );
    fc.features.forEach((f) => {
      f.properties.number = npsIds.indexOf(f.id) + 1;
    });
    fc.features.sort(function (a, b) {
      return a.properties.number - b.properties.number;
    });
    createNumbersLayer(fc.features);

    // return coords for feeding into directions api

    calculateRoutes(
      fc.features.map((f) => {
        return {
          id: f.id,
          coords: f.geometry.coordinates,
        };
      })
    );

    // get bounds for map
    fc.features.push(pointData.features[0]);
    const polygon = turf.convex(fc);
    const bbox = turf.bbox(polygon);
    map.fitBounds(bbox, {
      padding: 60,
    });

    // add active state to those 5 ids
    nps.forEach((p, i) => {
      map.setFeatureState(
        {
          source: 'locations',
          id: p.id,
        },
        {
          active: true,
        }
      );
    });
  }
};

const clearActivePoints = () => {
  nps.forEach((p) => {
    map.setFeatureState(
      {
        source: 'locations',
        id: p.id,
      },
      {
        active: false,
      }
    );
  });
};

const findCounty = (point) => {
  window.COUNTIES.features.forEach((f) => {
    const inside = turf.pointsWithinPolygon(point, f);
    if (inside.features.length > 0) {
      point.properties.county = f.properties.NAME;
    }
  });
};

const addAddressInfo = (point) => {
  const county = point.properties.county;
  const cds = window.COUNTY_DATES.counties;
  const vcOpen = cds.find((c) => c.name === county)['in_person'];
  const dropOpen = cds.find((c) => c.name === county)['drop'];
  const str = `${point.properties.name.replace(
    ', United States',
    ''
  )} is located in ${county} County. ${county} County drop-off centers open on ${dropOpen} and voting centers will open on ${vcOpen}.`;
  document.getElementById('address-info').innerHTML = str;
  document.getElementById('address-info').style.visibility = 'visible';
  document.getElementById('copyright-info').style.position = 'relative';
};

const updateMarker = (res) => {
  const coords = res.result.center;
  pointData.features[0].geometry.coordinates = coords;
  pointData.features[0].properties.name = res.result.place_name;
  findCounty(pointData.features[0]);
  map.getSource('point').setData(pointData);
  addAddressInfo(pointData.features[0]);
};

const addLocations = () => {
  let locData;
  const a = window.LOCATIONS.features.map((d) => d.properties.county);
  console.log(a);
  console.log([...new Set(a)]);
  if (vc && db) {
    locData = window.LOCATIONS;
  } else if (vc) {
    const types = [
      'Vote Center',
      'Vote Center - Drive thru',
      'Vote Center - Pop Up Voting',
      'Mega Vote Center',
      'Voter Assistance Center',
      'In-Person Voting',
      'Vote Early Site',
    ];
    locData = {
      type: 'FeatureCollection',
      features: window.LOCATIONS.features.filter(
        (f) => types.indexOf(f.properties.type) > -1
      ),
    };
  } else if (db) {
    const types = ['Ballot Drop Box', 'Ballot Drop-Box', 'Ballot Drop-Off'];
    locData = {
      type: 'FeatureCollection',
      features: window.LOCATIONS.features.filter(
        (f) => types.indexOf(f.properties.type) > -1
      ),
    };
  }
  map.addSource('locations', {
    type: 'geojson',
    data: locData,
  });

  map.addLayer(
    {
      id: 'locations-layer',
      type: 'circle',
      source: 'locations',
      paint: {
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'active'], false],
          10,
          5,
        ],
        'circle-color': [
          'match',
          ['get', 'type'],
          'Ballot Drop Box',
          dropColor,
          'Ballot Drop-Box',
          dropColor,
          'Ballot Drop-Off',
          dropColor,
          'Vote Center',
          centerColor,
          'Vote Center - Drive thru',
          centerColor,
          'Vote Center - Pop Up Voting',
          centerColor,
          'Mega Vote Center',
          centerColor,
          'Voter Assistance Center',
          centerColor,
          'In-Person Voting',
          centerColor,
          'Vote Early Site',
          centerColor,
          /* other */ '#ccc',
        ],
        'circle-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          6,
          0.3,
          7.5,
          0.4,
          9,
          1,
        ],
        'circle-stroke-color': '#04b867',
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'mouseover'], false],
          4,
          0,
        ],
      },
    },
    'settlement-minor-label'
  );

  map.on('mousemove', 'locations-layer', function (e) {
    if (e.features.length > 0) {
      d3.selectAll('li').classed('active', false);
      const listIds = nps.map((item) => item.id);
      map.setFeatureState(
        {
          source: 'locations',
          id: currentMouse,
        },
        {
          mouseover: false,
        }
      );
      if (listIds.indexOf(e.features[0].id) > -1) {
        d3.select('#info-box').classed('visible', false);
        d3.select(`#item-${e.features[0].id}`).classed('active', true);
        const top = document.getElementById(`item-${e.features[0].id}`)
          .offsetTop;
        document.getElementById('sidebar').scrollTop = top - 50;

        // make sure all filters are turned off for moble
        routes.features.forEach((r) => {
          map.setFeatureState(
            {
              source: 'routes',
              id: r.id,
            },
            {
              active: false,
            }
          );
        });

        // apply active filter
        routes.features
          .filter((r) => r.id === e.features[0].id)
          .forEach((r) => {
            map.setFeatureState(
              {
                source: 'routes',
                id: r.id,
              },
              {
                active: true,
              }
            );
          });
      } else {
        d3.select('#info-box').classed('visible', true);
        const loc = e.features[0].properties;
        document.getElementById('info-name').innerHTML = loc.name;
        document.getElementById('info-address').innerHTML = loc.address;
        document.getElementById('info-type').innerHTML = loc.type;
        d3.select('#info-type').attr('class', `type ${loc.type}`);
        document.getElementById('info-hours').innerHTML = loc.info;
        currentMouse = loc.id;
        map.setFeatureState(
          {
            source: 'locations',
            id: currentMouse,
          },
          {
            mouseover: true,
          }
        );
      }
    }
  });

  map.on('mouseleave', 'locations-layer', function (e) {
    routes.features.forEach((r) => {
      map.setFeatureState(
        {
          source: 'routes',
          id: r.id,
        },
        {
          active: false,
        }
      );
    });
    // d3.select('#info-box').classed('visible', false);
  });
};

/* Boot the map */
export const createMap = (styleUrl, view, opts = null) => {
  mapboxgl.accessToken =
    'pk.eyJ1IjoibGF0aW1lcyIsImEiOiJjajhvcXRraGUwNnlwMzNyczR3cTBsaWh1In0.0cPKLwe2A0ET4P5CtWSiLQ';
  const mapEl = document.getElementById('map').getBoundingClientRect();
  const mapDim = [mapEl.height, mapEl.width];
  let mapDict = {
    container: 'map',
    style: styleUrl,
    center: [-118.249905, 34.056602],
    zoom: 8,
    maxBounds: bounds,
  };
  map = new mapboxgl.Map(mapDict);
  window.MAP = map;
  map.addControl(new mapboxgl.NavigationControl(), 'top-right');
  map.on('load', function () {
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      bbox: [-120.081786, 32.0, -113.988302, 35.879721],
      country: 'US',
      flyTo: false,
      // mapboxgl: mapboxgl
    });
    addLocations();

    document.getElementById('geocoder').appendChild(geocoder.onAdd(map));

    // source can stay the whole time
    map.addSource('point', {
      type: 'geojson',
      data: pointData,
    });
    // add initial marker
    addMarker();

    // geocoder behaviors
    geocoder.on('result', (res) => {
      const testRoutes = map.getLayer('routes-layer');
      if (typeof testRoutes !== 'undefined') {
        map.removeLayer('routes-layer');
        map.removeSource('routes');
      }
      const testNums = map.getLayer('numbers-layer');
      if (typeof testNums !== 'undefined') {
        map.removeLayer('numbers-layer');
        map.removeSource('numbers');
      }
      updateMarker(res);
      clearActivePoints();
      getNearestPoints();
      d3.select('#list ul').html('');
      if (window.innerWidth < 800) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
      document.querySelectorAll('input[type=text]')[0].blur();
    });

    geocoder.on('clear', (res) => {
      // just move it off the map
      updateMarker([0, 0]);
      clearActivePoints();
      map.removeLayer('numbers-layer');
      map.removeSource('numbers');
    });
  });
  return map;
};

d3.selectAll('.icon').on('click', (d) => {
  const classes = d.target.classList;
  const type = classes[1];
  if (classes[2] === 'disabled') {
    d3.select(`.${type}`).classed(`disabled`, false);
    if (type === 'voting') {
      vc = true;
    } else if (type === 'drop') {
      db = true;
    }
  } else {
    if (type === 'voting' && !db) {
      db = true;
      d3.select(`.drop`).classed(`disabled`, false);
      // turn drop box on
    } else if (type === 'drop' && !vc) {
      vc = true;
      d3.select(`.voting`).classed(`disabled`, false);
      // turn voting on
    }
    d3.select(`.${type}`).classed(`disabled`, true);
    if (type === 'voting') {
      vc = false;
    } else if (type === 'drop') {
      db = false;
    }
  }
  map.removeLayer('locations-layer');
  map.removeSource('locations');
  addLocations();
  clearActivePoints();
  const testRoutes = map.getLayer('routes-layer');
  if (typeof testRoutes !== 'undefined') {
    map.removeLayer('routes-layer');
    map.removeSource('routes');
  }
  const testNums = map.getLayer('numbers-layer');
  if (typeof testNums !== 'undefined') {
    map.removeLayer('numbers-layer');
    map.removeSource('numbers');
  }
  getNearestPoints();
  if (vc && db) {
    d3.select(`.and`).classed(`disabled`, false);
  } else {
    d3.select(`.and`).classed(`disabled`, true);
  }
});
